import { DeleteAccountTypeEnum } from 'api/enums';

interface IPrivacyPolicyProps {
    type?: DeleteAccountTypeEnum;
}
export default function TermsAndConditions(props: IPrivacyPolicyProps) {
    const { type } = props;
    return (
        <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="px-8 py-10 sm:px-12 sm:py-12">
                    <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms and Conditions</h1>

                    <section className="mb-8">
                        <p className="text-gray-700 leading-relaxed">
                            Welcome to{' '}
                            {type === DeleteAccountTypeEnum.PROGRESS_CENTER ? 'ProgressCenter' : 'SiteWalk Capture'} ("{' '}
                            {type === DeleteAccountTypeEnum.PROGRESS_CENTER ? 'ProgressCenter' : 'SiteWalk Capture'},"
                            "we," "us," or "our"). These Terms and Conditions ("Terms") govern your use of our software
                            platform, services, and website located at{' '}
                            <a
                                href="https://v4.progresscenter.io"
                                className="text-blue-600 hover:text-blue-800 underline"
                            >
                                https://v4.progresscenter.io
                            </a>{' '}
                            (collectively, the "Service").
                        </p>
                        <p className="text-gray-700 leading-relaxed mt-4">
                            By accessing or using the Service, you agree to comply with these Terms. If you do not
                            agree, you must not access or use the Service.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Use of the Service</h2>
                        <div className="space-y-3 text-gray-700 leading-relaxed">
                            <p>The Service is provided for professional and business use.</p>
                            <p>
                                You may access and use the Service solely in accordance with these Terms and any
                                applicable subscription or license agreement.
                            </p>
                            <p>
                                You are responsible for ensuring that your use of the Service complies with all
                                applicable laws and regulations in your jurisdiction.
                            </p>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Account Registration</h2>
                        <p className="text-gray-700 leading-relaxed mb-3">
                            To access certain features, you must create an account. You agree to:
                        </p>
                        <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Provide accurate and complete information during registration;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Maintain the security of your login credentials;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Notify us immediately of any unauthorized access or security breach.</span>
                            </li>
                        </ul>
                        <p className="text-gray-700 leading-relaxed mt-4">
                            You are responsible for all activities under your account.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Subscription, Fees, and Payments</h2>
                        <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Access to certain features or plans may require a paid subscription.</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>
                                    Fees, billing cycles, and payment methods are outlined at the time of subscription.
                                </span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>
                                    Subscriptions automatically renew unless canceled prior to the renewal date.
                                </span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>All payments are non-refundable unless otherwise stated in writing.</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>We reserve the right to modify pricing or plans with prior notice.</span>
                            </li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Intellectual Property Rights</h2>
                        <p className="text-gray-700 leading-relaxed mb-3">
                            All software, content, designs, documentation, and data generated by or displayed on the
                            Service are the property of{' '}
                            {type === DeleteAccountTypeEnum.PROGRESS_CENTER ? 'ProgressCenter' : 'SiteWalk Capture'} or
                            its licensors. You are granted a non-exclusive, non-transferable, limited license to access
                            and use the Service for internal business purposes only.
                        </p>
                        <p className="text-gray-700 leading-relaxed mb-3">You may not:</p>
                        <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Reverse engineer, decompile, or modify the software;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Copy, distribute, or publicly display any portion of the Service;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Use the Service to build a competing product or service.</span>
                            </li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">User Content and Data</h2>
                        <p className="text-gray-700 leading-relaxed mb-3">
                            By uploading data, images, or content to the platform, you grant{' '}
                            {type === DeleteAccountTypeEnum.PROGRESS_CENTER ? 'ProgressCenter' : 'SiteWalk Capture'} a
                            limited, revocable license to store, process, and display such content solely for the
                            purpose of providing the Service. You retain all ownership rights to your data.
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                            We may use aggregated or anonymized data for analytics, product improvement, or research
                            purposes — never in a form that identifies you or your organization.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Protection and Privacy</h2>
                        <p className="text-gray-700 leading-relaxed mb-3">
                            Your personal and project data are handled in accordance with our{' '}
                            <a href="/privacy-policy" className="text-blue-600 hover:text-blue-800 underline">
                                Privacy Policy
                            </a>
                            .
                        </p>
                        <p className="text-gray-700 leading-relaxed mb-3">
                            We implement technical and organizational measures to safeguard data against unauthorized
                            access, loss, or alteration.
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                            If you process data on behalf of third parties, you are solely responsible for ensuring
                            compliance with applicable data protection regulations (e.g., GDPR, CCPA, or India's DPDP
                            Act).
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Acceptable Use Policy</h2>
                        <p className="text-gray-700 leading-relaxed mb-3">You agree not to:</p>
                        <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Attempt to gain unauthorized access to any systems or networks;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Upload or transmit malicious code or harmful software;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Use the Service for unlawful, fraudulent, or misleading activities;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Interfere with the performance or security of the Service;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Violate intellectual property, privacy, or data protection rights.</span>
                            </li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Service Availability and Support</h2>
                        <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>
                                    We strive to ensure continuous, reliable service but cannot guarantee uninterrupted
                                    uptime.
                                </span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>
                                    Scheduled maintenance or technical updates may cause temporary service
                                    interruptions.
                                </span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>
                                    Support requests can be submitted via our official contact channels or support
                                    portal.
                                </span>
                            </li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                            Third-Party Services and Integrations
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-3">
                            The Service may integrate with third-party APIs, tools, or cloud services.
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                            We are not responsible for the operation, privacy, or performance of third-party services.
                            Use of such integrations is at your own discretion.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Disclaimer of Warranties</h2>
                        <p className="text-gray-700 leading-relaxed mb-3">
                            The Service is provided "as is" and "as available."{' '}
                            {type === DeleteAccountTypeEnum.PROGRESS_CENTER ? 'ProgressCenter' : 'SiteWalk Capture'}{' '}
                            makes no warranties, express or implied, regarding:
                        </p>
                        <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>The accuracy or completeness of data or analytics results;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Continuous or error-free operation;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Fitness for a particular purpose or merchantability.</span>
                            </li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Limitation of Liability</h2>
                        <p className="text-gray-700 leading-relaxed mb-3">
                            To the fullest extent permitted by law,{' '}
                            {type === DeleteAccountTypeEnum.PROGRESS_CENTER ? 'ProgressCenter' : 'SiteWalk Capture'},
                            its affiliates, and partners shall not be liable for:
                        </p>
                        <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2 mt-1">•</span>
                                <span>Any indirect, incidental, special, or consequential damages;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Data loss, revenue loss, or business interruption;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>
                                    Total liability exceeding the fees paid to{' '}
                                    {type === DeleteAccountTypeEnum.PROGRESS_CENTER
                                        ? 'ProgressCenter'
                                        : 'SiteWalk Capture'}{' '}
                                    in the previous six (6) months.
                                </span>
                            </li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Indemnification</h2>
                        <p className="text-gray-700 leading-relaxed mb-3">
                            You agree to indemnify and hold harmless{' '}
                            {type === DeleteAccountTypeEnum.PROGRESS_CENTER ? 'ProgressCenter' : 'SiteWalk Capture'} and
                            its affiliates from any claims, damages, liabilities, or expenses arising from:
                        </p>
                        <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Your misuse of the Service;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Violation of these Terms;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Infringement of third-party rights.</span>
                            </li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Termination</h2>
                        <p className="text-gray-700 leading-relaxed mb-3">
                            We may suspend or terminate your access to the Service at any time if you:
                        </p>
                        <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Violate these Terms;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Engage in fraudulent or abusive activities;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Fail to pay fees when due.</span>
                            </li>
                        </ul>
                        <p className="text-gray-700 leading-relaxed mt-4">
                            Upon termination, your license to use the Service will immediately cease, and we may delete
                            your stored data after a retention grace period.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Governing Law and Jurisdiction</h2>
                        <p className="text-gray-700 leading-relaxed mb-3">
                            These Terms are governed by and construed under the laws of [Insert Applicable Jurisdiction
                            — e.g., India, Saudi Arabia, or UAE], without regard to conflict of law principles.
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                            Any disputes shall be resolved exclusively in the courts of [Insert City, Country].
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Amendments to These Terms</h2>
                        <p className="text-gray-700 leading-relaxed">
                            We may revise or update these Terms from time to time. Updated versions will be posted on
                            this page with a new effective date. Continued use of the Service constitutes acceptance of
                            the revised Terms.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Information</h2>
                        <p className="text-gray-700 leading-relaxed">
                            For questions regarding these Terms, please contact:
                        </p>
                        <p className="text-gray-700 leading-relaxed mt-3">
                            <span className="font-medium">Email:</span>{' '}
                            <a
                                href="mailto:info@timelapseme.com"
                                className="text-blue-600 hover:text-blue-800 underline"
                            >
                                info@timelapseme.com
                            </a>
                        </p>
                    </section>

                    <div className="mt-12 pt-8 border-t border-gray-200">
                        <p className="text-sm text-gray-500 text-center">
                            © {new Date().getFullYear()}{' '}
                            {type === DeleteAccountTypeEnum.PROGRESS_CENTER ? 'ProgressCenter' : 'SiteWalk Capture'}.
                            All rights reserved.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
